// מסך 2 — רשימת לקוחות: סינון פעיל/ארכיון, חיפוש, יצירה.
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Empty, ErrorBox, Field, Loading, Modal, fmtDate, useLoad, useMe } from '../components/ui';
import { PageHead, useAction } from '../components/mgmt/common';
import { LinksEditor, type LinkItem } from '../components/mgmt/LinksEditor';

interface ClientRow { id: string; name: string; slug: string; status: string; notes: string; is_demo: boolean; brand_count: number; campaign_count: number; updated_at: string; created_at: string }

export default function Clients() {
  const me = useMe();
  const [status, setStatus] = useState<'active' | 'archived' | 'all'>('active');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const { data, error, loading, reload } = useLoad<ClientRow[]>(`/api/clients?status=${status}`);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data ?? []).filter((c) => !s || c.name.toLowerCase().includes(s) || c.slug.includes(s) || c.notes.toLowerCase().includes(s));
  }, [data, q]);
  const canEdit = me.role !== 'viewer';
  return (
    <div>
      <PageHead title="לקוחות" sub="לקוח מכיל מותגים, חומרים וקמפיינים. מחיקה = העברה לארכיון.">
        {canEdit && <button className="btn primary" onClick={() => setCreating(true)}>לקוח חדש</button>}
      </PageHead>
      <div className="toolbar">
        <Field label="חיפוש"><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="שם, מזהה או הערה" /></Field>
        <Field label="סטטוס">
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="active">פעילים</option><option value="archived">ארכיון</option><option value="all">הכל</option>
          </select>
        </Field>
      </div>
      <ErrorBox error={error} />
      {loading && !data && <Loading />}
      {data && rows.length === 0 && <Empty>{q ? 'אין תוצאות לחיפוש.' : status === 'archived' ? 'אין לקוחות בארכיון.' : 'אין לקוחות עדיין.'}</Empty>}
      {rows.length > 0 && (
        <table className="table cards-sm">
          <thead><tr><th>לקוח</th><th>מזהה</th><th>מותגים</th><th>קמפיינים</th><th>סטטוס</th><th>עודכן</th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td><Link to={`/clients/${c.id}`}><strong>{c.name}</strong></Link>{c.is_demo && <> <span className="badge warn">הדגמה</span></>}</td>
                <td data-l="מזהה"><span className="mono">{c.slug}</span></td>
                <td data-l="מותגים">{c.brand_count}</td>
                <td data-l="קמפיינים">{c.campaign_count}</td>
                <td data-l="סטטוס"><span className={`badge ${c.status === 'active' ? 'ok' : ''}`}>{c.status === 'active' ? 'פעיל' : 'ארכיון'}</span></td>
                <td data-l="עודכן" className="small muted">{fmtDate(c.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {creating && <NewClientModal onClose={() => setCreating(false)} onCreated={reload} />}
    </div>
  );
}

function NewClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [notes, setNotes] = useState('');
  const [links, setLinks] = useState<LinkItem[]>([]);
  const { busy, error, run } = useAction();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const c = await run(() => api.post<{ id: string }>('/api/clients', { name, slug: slug || undefined, notes, links: links.filter((l) => l.url) }), 'הלקוח נוצר');
    if (c) { onCreated(); nav(`/clients/${c.id}`); }
  };
  return (
    <Modal title="לקוח חדש" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <Field label="שם הלקוח *"><input required value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
        <Field label="מזהה (לא חובה)" hint="לטיני; אם ריק ייווצר אוטומטית"><input className="ltr" value={slug} onChange={(e) => setSlug(e.target.value)} /></Field>
        <Field label="הערות"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        <LinksEditor value={links} onChange={setLinks} />
        <ErrorBox error={error} />
        <div className="row"><button className="btn primary" disabled={busy || !name.trim()}>{busy ? 'יוצר…' : 'יצירה'}</button><button type="button" className="btn" onClick={onClose}>ביטול</button></div>
      </form>
    </Modal>
  );
}
